import React, { useRef, useState } from "react";
import { api, resolveUrl } from "../lib/api";
import { Upload, RotateCcw, Check } from "lucide-react";

function previewSrc(slot) {
  const token = localStorage.getItem("krs_token");
  if (slot.has_draft) {
    return resolveUrl(`/api/images/${slot.key}?draft=1&t=${token}&c=${encodeURIComponent(slot.updated_at || "")}`);
  }
  if (slot.draft_delete) return slot.default_url;
  if (slot.live) return resolveUrl(`/api/images/${slot.key}?v=${encodeURIComponent(slot.published_at || "")}`);
  return slot.default_url;
}

function ImageSlot({ slot, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Please choose an image file."); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/images/${slot.key}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onChange();
    } catch (e) {
      alert(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const revert = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/images/${slot.key}`);
      await onChange();
    } finally {
      setBusy(false);
    }
  };

  const canReset = slot.live || slot.has_draft || slot.draft_delete;

  return (
    <div className="bg-white border border-cream-dark">
      <div className="relative h-44 bg-cream-dark overflow-hidden group">
        <img src={previewSrc(slot)} alt={slot.label} className="w-full h-full object-cover" />
        {slot.dirty && (
          <span className="absolute top-2 left-2 bg-gold text-navy text-[9px] tracking-wide-nav uppercase px-2 py-1 font-sans">Pending</span>
        )}
        {!slot.dirty && slot.live && (
          <span className="absolute top-2 left-2 bg-navy text-cream text-[9px] tracking-wide-nav uppercase px-2 py-1 font-sans">Live</span>
        )}
        <div className="absolute inset-0 bg-navy/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <button onClick={() => fileRef.current?.click()} disabled={busy} className="bg-cream text-navy px-4 py-2 font-sans text-[10px] tracking-wide-nav uppercase hover:bg-gold flex items-center gap-1.5"><Upload size={13} /> Replace</button>
          {canReset && (
            <button onClick={revert} disabled={busy} className="bg-navy text-cream px-4 py-2 font-sans text-[10px] tracking-wide-nav uppercase hover:bg-navy-light flex items-center gap-1.5"><RotateCcw size={13} /> Reset</button>
          )}
        </div>
        {busy && <div className="absolute inset-0 bg-cream/70 flex items-center justify-center"><span className="font-sans text-[10px] tracking-wide-nav uppercase text-navy">Working…</span></div>}
      </div>
      <div className="px-4 py-3">
        <p className="font-sans text-sm text-navy">{slot.label}</p>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}

export default function ImageManager({ images, onChange }) {
  const groups = [];
  const map = {};
  images.forEach((s) => {
    if (!map[s.group]) { map[s.group] = []; groups.push(s.group); }
    map[s.group].push(s);
  });

  return (
    <div className="space-y-12">
      {groups.map((g) => (
        <div key={g}>
          <h3 className="font-serif text-2xl text-navy mb-1">{g}</h3>
          <div className="w-12 h-px bg-gold mb-6" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {map[g].map((slot) => (
              <ImageSlot key={slot.key} slot={slot} onChange={onChange} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
